import axios from 'axios'
import { authToken } from './authentication.script'
import { sanitizeFilename } from './document.filename.script'

export interface BlobExportResult {
  status: number
  blob?: Blob
  filename?: string
  error?: string
}

/**
 * Parse the filename from a Content-Disposition header.
 * Prioritises RFC 5987 `filename*=UTF-8''…` (URL-decoded),
 * then falls back to `filename="…"` or `filename=…`.
 */
function parseContentDispositionFilename(header: string): string | null {
  if (!header) return null

  // 1. filename*=UTF-8''encoded_name  (RFC 5987)
  const starMatch = header.match(/filename\*\s*=\s*UTF-8'[^']*'([^;\s]+)/i)
  if (starMatch) {
    try {
      return decodeURIComponent(starMatch[1])
    } catch { /* fall through */ }
  }

  // 2. filename="name" (quoted)
  const quotedMatch = header.match(/filename\s*=\s*"((?:[^"\\]|\\.)*)"/i)
  if (quotedMatch) {
    return quotedMatch[1].replace(/\\(.)/g, '$1')
  }

  // 3. filename=name (unquoted)
  const plainMatch = header.match(/filename\s*=\s*([^;\s]+)/i)
  if (plainMatch) {
    return plainMatch[1]
  }

  return null
}

export async function fetchExportBlob(
  url: string,
  params: Record<string, string | undefined>,
  fallbackName?: string
): Promise<BlobExportResult> {
  const cleanParams: Record<string, string> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v) cleanParams[k] = v
  }

  try {
    const response = await axios.get(url, {
      headers: { Authorization: authToken() },
      responseType: 'blob',
      params: cleanParams,
    })

    const disposition = response.headers['content-disposition'] ?? ''
    const serverName = parseContentDispositionFilename(disposition)
    const format = cleanParams.format ?? 'xlsx'
    const filename = serverName ?? fallbackName ?? `export_${new Date().toISOString().slice(0, 10)}.${format}`

    return { status: response.status, blob: response.data, filename }
  } catch (error: any) {
    const status = error.response?.status ?? 500
    let errorMessage = ''

    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text()
        const json = JSON.parse(text)
        errorMessage = json?.error?.message ?? json?.message ?? text
      } catch {
        errorMessage = 'Export failed'
      }
    } else {
      errorMessage = error.response?.data?.error?.message ?? error.message ?? 'Export failed'
    }

    return { status, error: errorMessage }
  }
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = sanitizeFilename(filename)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
